import { motion } from "framer-motion";
import { useState } from "react";
import { useTheme } from "styled-components";

const PATH_LENGTH = 603;

interface LoadingSpinnerProps {
  loading?: boolean;
  onComplete?: () => void;
}

// onComplete is intended to fire after the animation has finished.
export default function AnimatedLoadingSpinner({
  loading = true,
  onComplete,
}: LoadingSpinnerProps) {
  const [isCompleting, setIsCompleting] = useState(!loading);

  const [render, rerender] = useState(0);
  const theme = useTheme();

  if (loading && isCompleting) {
    setIsCompleting(false);
    rerender(render + 1);
  }

  return (
    <div>
      <svg
        key={isCompleting ? "completing" : "loading"}
        width="120px"
        height="120px"
        viewBox="0 0 256 256"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Quarter circle spinner (loading) + Full circle (completing). */}
        <motion.circle
          // key={render}
          cx="128"
          cy="128"
          r="96"
          fill="none"
          stroke={theme.colors.N800}
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={PATH_LENGTH}
          strokeDashoffset={PATH_LENGTH * 0.75}
          initial={{ rotate: -90 }}
          animate={isCompleting ? { strokeDashoffset: 0 } : { rotate: 270 }}
          transition={{
            duration: isCompleting ? 0.4 : 0.75,
            repeat: 0,
            ease: isCompleting ? "easeInOut" : "linear",
          }}
          onAnimationComplete={() => {
            if (isCompleting) {
              onComplete?.();
            } else if (!loading) {
              setIsCompleting(true);
            } else {
              rerender(render + 1);
            }
          }}
        />

        {/* Checkmark. */}
        {isCompleting && (
          <motion.path
            d="M88 132l24 24 56-56"
            fill="none"
            stroke={theme.colors.N800}
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="120"
            strokeDashoffset="120"
            animate={{ strokeDashoffset: 0 }}
            transition={{ duration: 0.25, delay: 0.2, ease: "easeInOut" }}
          />
        )}
      </svg>
    </div>
  );
}
